import axios from "axios";

// The web portal talks to the SHARED live ARV backend (single writer), not this app's own server.
const BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const api = axios.create({ baseURL: API, timeout: 60000 });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("arv_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Global 401 handling: tokens are revoked on password/role/2FA change and expire sooner.
// On any 401 from an AUTHENTICATED request, end the session and send back to login.
let _redirecting = false;
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const hadAuth = !!error?.config?.headers?.Authorization;
    // Paywall: a 402 with SUBSCRIPTION_REQUIRED means the company's trial ended.
    // Send them to the Subscribe screen WITHOUT ending their session.
    const detail = error?.response?.data?.detail;
    // Agreement gate: trial over and the NDA/AVG agreement is not signed yet (or signing is
    // required before checkout). Send the user to the Sign agreement screen first.
    if ((status === 402 || status === 403) && detail?.code === "NDA_SIGNATURE_REQUIRED") {
      const path = window.location.pathname;
      const onSign = path.startsWith("/sign-agreement");
      if (!onSign && !_redirecting) {
        _redirecting = true;
        window.location.assign("/sign-agreement");
      }
      return Promise.reject(error);
    }
    if (status === 402 && (detail?.code === "SUBSCRIPTION_REQUIRED")) {
      const path = window.location.pathname;
      const onBilling = path.startsWith("/subscribe") || path.startsWith("/billing-");
      if (!onBilling && !_redirecting) {
        _redirecting = true;
        window.location.assign("/subscribe");
      }
      return Promise.reject(error);
    }
    if (status === 401 && hadAuth) {
      localStorage.removeItem("arv_token");
      const path = window.location.pathname;
      const onLogin = path === "/login" || path === "/admin/login";
      if (!onLogin && !_redirecting) {
        _redirecting = true;
        window.location.assign(path.startsWith("/admin") ? "/admin/login" : "/login");
      }
    }
    // Company un-approved mid-session: end session and send back to login.
    if (status === 403 && detail?.code === "COMPANY_APPROVAL_PENDING" && hadAuth) {
      localStorage.removeItem("arv_token");
      const path = window.location.pathname;
      const onLogin = path === "/login" || path === "/admin/login";
      if (!onLogin && !_redirecting) {
        _redirecting = true;
        window.location.assign(path.startsWith("/admin") ? "/admin/login" : "/login");
      }
    }
    return Promise.reject(error);
  }
);

export function apiError(e) {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  if (d && typeof d === "object") return d.message || d.code || "Something went wrong";
  // Upstream gateway/origin errors (e.g. Cloudflare 5xx HTML) or timeouts — never surface raw HTML.
  if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) return "That took too long. Please try again in a moment.";
  if (status >= 500) return "The server had a problem. Please try again in a moment.";
  return e?.message || "Something went wrong";
}

// Client-side password policy mirror of the server (min 10, >=1 letter, >=1 number).
export function passwordError(pw) {
  if (!pw || pw.length < 10) return "Password must be at least 10 characters";
  if (!/[A-Za-z]/.test(pw)) return "Password must include at least one letter";
  if (!/\d/.test(pw)) return "Password must include at least one number";
  return null;
}

// Public file URLs (report PDF, part QR, sparebook drawings) served by id
export const fileUrl = (path) => `${API}/${path}`;

export { API };
export const API_ORIGIN = BASE;
export default api;
